import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { SubSink } from 'subsink';
import { UserService } from 'src/app/shared/service/user.service';
import { WithdrawModel, WithdrawRialStatus } from 'src/app/shared/models/withdraw.model';
import Swal from 'sweetalert2';
import { HttpError } from 'src/app/shared/models/http-error.model';
import { NoteComponent } from '../../../../../shared/components/note/note.component';
import { NbDialogRef, NbDialogService, NbTabComponent } from '@nebular/theme';
import Big from 'big.js';
import { EthereumCompatibleBlockchain } from '../../../../../shared/service/crypto-withdraw/ethereum-compatible';
import { SupportedNetworks } from '../../../../../shared/service/crypto-withdraw/blockchain';
import { TonBlockchain } from '../../../../../shared/service/crypto-withdraw/ton-blockchain';
import { MarketService } from '../../../../../shared/service/market.service';
import { CryptoWithdrawService } from '../../../../../shared/service/crypto-withdraw.service';
import { take } from 'rxjs/operators';

declare const window: any;

type SupportedContract = 'USDT' | 'USDC';

@Component({
  selector: 'app-withdraw',
  templateUrl: './withdraw.component.html',
  styleUrls: ['./withdraw.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WithdrawComponent implements OnInit, OnDestroy {
  @ViewChild('SeedPhraseTemplate', { static: true }) seedPhraseTemplate!: TemplateRef<any>;
  tableLoading = true;
  sub = new SubSink();
  withdraws: WithdrawModel[] = [];
  needConfirmationList: WithdrawModel[] = [];
  waitingForProcessingList: WithdrawModel[] = [];
  under10TonRequests: WithdrawModel[] = [];
  hasMessage: WithdrawModel[] = [];
  activeTab = '0';
  errors: any = {};
  Toast = Swal.mixin({
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });
  tronLinkLoading = false;
  saveLoading = false;
  selectedWithdraw!: WithdrawModel;
  withdrawAlert: { show: boolean; silent: boolean };
  contractAddress: {
    [key in SupportedContract]: string;
  } = {
    USDT: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t',
    USDC: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8'
  };
  ethereumCompatibleBlockChain!: EthereumCompatibleBlockchain;
  tonBlockchain!: TonBlockchain;
  markets: Map<string, {
    usdtPrice: number,
    irrPrice: number
  }> = new Map();
  sendTransactionLoading = false;
  seed?: string;
  limitChecked = 50;
  limitPending = 10;
  limitUnderXTon = 50;
  limitHasMessage = 50;
  v5Wallet = false;

  findWithdraw(withdrawId: number): WithdrawModel | undefined {
    return this.getWithdrawList().find(withdraw => withdraw.id === withdrawId);
  }

  getBatchTransferSumAmount(currencyId: string) {
    let sum = Big(0);
    const ids = this.tonBlockchain.getBatchTransferIds();
    for (const id of ids) {
      const withdraw = this.findWithdraw(id);
      if (withdraw && withdraw.currency.id === currencyId) {
        sum = sum.add(Big(withdraw.amount).sub(withdraw.fee).round(4, Big.roundDown));
      }
    }
    return sum.toNumber();
  }

  setTransactionHashInsideWithdraw(withdrawId: number, hashText: string): void {
    const foundedWithdraw = this.findWithdraw(withdrawId);
    if (foundedWithdraw) {
      foundedWithdraw.message = hashText;
      foundedWithdraw.status = WithdrawRialStatus.DONE;
      this._cd.detectChanges();
    }
  }

  checkIfEthCompatible(withdraw: string) {
    return this.ethereumCompatibleBlockChain.isNetworkSupported(withdraw);
  }


  changeBatchTransferIdSelection(isSelected: boolean, id: number): void {
    if (isSelected) {
      const withdraw = this.getWithdrawList().find(withdraw => withdraw.id === id);
      if (withdraw && this.tonBlockchain.checkAddressIsValid(withdraw.wallet)) {
        this.tonBlockchain.addIDToTransferList(id);
      } else {
        this.Toast.fire({ icon: 'error', text: 'آدرس یک آدرس معتبر در شبکه تن نمی‌باشد.' });
      }
    } else {
      this.tonBlockchain.removeIDFromTransferList(id);
    }
  }

  openSeedDialog(): void {
    this.dialogService.open(this.seedPhraseTemplate, {});
  }

  sendBatchTransaction() {
    this.sendTransactionLoading = true;
    this.tableLoading = true;
    const s = this.seed as string;
    this.seed = undefined;
    this.cryptoWithdrawService.sendBatchTransaction(this.tonBlockchain.getBatchTransferIds(), s, this.v5Wallet).subscribe((hash: string) => {
      const ids = this.tonBlockchain.getBatchTransferIds();
      this.Toast.fire({ icon: 'success', text: 'درخواست برداشت با موفقیت انجام شد هش تراکنش در قسمت پیغام قرار گرفت.', timer: 5000 });
      this.tableLoading = false;
      this.deSelectAllWithdraw();
      for (const id of ids) {
        this.setTransactionHashInsideWithdraw(id, hash);
      }
      this.sendTransactionLoading = false;
      this.v5Wallet = false;
    }, (response: any) => {
      this.v5Wallet = false;
      this.Toast.fire({ icon: 'error', text: response.error });
      this.sendTransactionLoading = false;
      this.tableLoading = false;
    });
  }


  async sendUsingTonkeeper(withdraw: WithdrawModel) {
    try {
      await this.tonBlockchain.connect();
    } catch (err) {
      this.Toast.fire({
        icon: 'success',
        text: `اشکالی در اتصال با TONKeeper به وجود آمده است لطفا مشکل را با تیم فنی مطرح کنید.`
      });
    }
    const transferAmount = Big(withdraw.amount).sub(withdraw.fee).round(4, Big.roundDown);
    const isNativeToken = this.tonBlockchain.checkIsNativeToken(withdraw.currency.id, withdraw.currency.network as SupportedNetworks);
    if (isNativeToken) {
      try {
        withdraw.message = await this.tonBlockchain.sendTransaction(withdraw.wallet, transferAmount.toString(), withdraw.tag);
        this.Toast.fire({
          icon: 'success',
          text: `تراکنش ارسال شد هش تراکنش: ${withdraw.message}`
        });
      } catch (err) {
        this.Toast.fire({
          icon: 'error',
          text: `مشکلی در ارسال تراکنش به وجود آمده است لطفا ارور کنسول را به تیم فنی ارجاع دهید..`
        });
        console.error(err);
      }
    } else {
      try {
        const transaction = await this.tonBlockchain.sendToken(withdraw.wallet, withdraw.currency.id, Big(transferAmount).toString(), withdraw.currency.network as SupportedNetworks, withdraw.tag);
        this.Toast.fire({
          icon: 'success',
          text: `تراکنش ارسال شد هش تراکنش: ${transaction}`
        });
        this.setTransactionHashInsideWithdraw(withdraw.id, `https://tonviewer.com/transaction/${transaction}`);
      } catch (error: any) {
        if (error.message === 'Contract address is not defined') {
          this.Toast.fire({
            icon: 'error',
            text: `این توکن پشتیبانی نمی‌شود جهت اضافه شدن با تیم فنی در ارتباط باشید.`
          });
        } else {
          this.Toast.fire({
            icon: 'error',
            text: `مشکلی در ارسال تراکنش به وجود آمده است لطفا به تیم فنی اطلاع دهید.`
          });
          console.error(error);
        }
      }
    }
  }

  getMarkets() {
    this.marketService.getMarkets().subscribe(markets => {
      for (const market of markets) {
        let marketMap = this.markets.get(market.baseCurrency.id);
        if (!marketMap) {
          marketMap = {
            usdtPrice: 0,
            irrPrice: 0
          };
        }
        if (market.quoteCurrency.id === 'IRR') {
          marketMap.irrPrice = market.lastPrice;
        }
        if (market.quoteCurrency.id === 'USDT') {
          marketMap.usdtPrice = market.lastPrice;
        }
        this.markets.set(market.baseCurrency.id, marketMap);
      }
    });
    this.getWithdraws();

  }

  selectExactNumberOfWithdraw(inputEvent: Event) {
    let numberOfSelected: number | undefined;
    if (inputEvent && (inputEvent.target as HTMLInputElement).value) {
      numberOfSelected = parseInt((inputEvent.target as HTMLInputElement).value);
      this.deSelectAllWithdraw();
      if (this.tonBlockchain.getBatchTransferIds().length < numberOfSelected) {
        this.selectAllWithdraw(numberOfSelected);
      } else if (this.tonBlockchain.getBatchTransferIds().length > numberOfSelected) {
        this.deSelectAllWithdraw(numberOfSelected);
      }
    }

  }

  selectAllWithdraw(numberOfSelected?: number) {
    this.tonBlockchain.addIDToTransferList(...this.getWithdrawList().filter(withdraw => withdraw.currency.network === 'TON').filter(withdraw => this.tonBlockchain.checkAddressIsValid(withdraw.wallet)).filter((_, index) => numberOfSelected ? index < numberOfSelected : index < 50).map(withdraw => withdraw.id));
  }

  deSelectAllWithdraw(numberOfSelected?: number) {
    this.tonBlockchain.removeIDFromTransferList(...this.getWithdrawList().map(withdraw => withdraw.id).filter((_, index) => numberOfSelected ? index < numberOfSelected : true));
  }

  async sendUsingMetaMask(withdraw: WithdrawModel) {
    await this.connectToMetaMask();
    const transferAmount = Big(withdraw.amount).sub(withdraw.fee);
    await this.ethereumCompatibleBlockChain.selectCorrectNetwork(withdraw.currency.network as SupportedNetworks);
    const isNativeToken = this.ethereumCompatibleBlockChain.checkIsNativeToken(withdraw.currency.id, withdraw.currency.network as SupportedNetworks);
    if (isNativeToken) {
      try {
        if (Big(await this.ethereumCompatibleBlockChain.getBalanceNativeToken()).lt(transferAmount)) {
          this.Toast.fire({
            icon: 'error',
            text: `موجودی کیف پول کافی نمی‌باشد.`
          });
          return;
        }
        const transaction = await this.ethereumCompatibleBlockChain.sendTransaction(withdraw.wallet, transferAmount.toString());
        const hash = this.ethereumCompatibleBlockChain.getTransactionLink(withdraw.currency.network as SupportedNetworks, transaction);
        this.setTransactionHashInsideWithdraw(withdraw.id, hash);

        this.Toast.fire({
          icon: 'success',
          text: `تراکنش ارسال شد هش تراکنش: ${transaction}`
        });
      } catch (error) {
        this.Toast.fire({
          icon: 'error',
          text: `مشکلی در ارسال تراکنش به وجود آمده است لطفا به تیم فنی اطلاع دهید.`
        });
        console.error(error);
      }
    } else {
      try {
        if (Big(await this.ethereumCompatibleBlockChain.getBalanceToken(withdraw.currency.id, withdraw.currency.network as SupportedNetworks)).lt(transferAmount)) {
          this.Toast.fire({
            icon: 'error',
            text: `موجودی کیف پول کافی نمی‌باشد.`
          });
          return;
        }
        const transaction = await this.ethereumCompatibleBlockChain.sendToken(withdraw.wallet, withdraw.currency.id, Big(transferAmount).toString(), withdraw.currency.network as SupportedNetworks);
        const hash = this.ethereumCompatibleBlockChain.getTransactionLink(withdraw.currency.network as SupportedNetworks, transaction);
        this.setTransactionHashInsideWithdraw(withdraw.id, hash);
        this.Toast.fire({
          icon: 'success',
          text: `تراکنش ارسال شد هش تراکنش: ${transaction}`
        });
      } catch (error: any) {
        if (error.message === 'Contract address is not defined') {
          this.Toast.fire({
            icon: 'error',
            text: `این توکن پشتیبانی نمی‌شود جهت اضافه شدن با تیم فنی در ارتباط باشید.`
          });
        } else {
          this.Toast.fire({
            icon: 'error',
            text: `مشکلی در ارسال تراکنش به وجود آمده است لطفا به تیم فنی اطلاع دهید.`
          });
          console.error(error);
        }
      }
    }

  }

  async connectToMetaMask() {
    try {
      await this.ethereumCompatibleBlockChain.connect();
    } catch (err: any) {
      if (err.message === 'Wallet address is required') {
        this.Toast.fire({
          icon: 'error',
          text: `اشکال در دریافت آدرس کیف پول در متامسک`
        });
      } else {
        this.Toast.fire({
          icon: 'error',
          text: `اشکال در اتصال به متامسک لطفا موضوع را به تیم فنی گزارش دهدید`
        });
        console.error(err);
      }
    }
  }

  constructor(
    private userService: UserService,
    private dialogService: NbDialogService,
    private marketService: MarketService,
    private cryptoWithdrawService: CryptoWithdrawService,
    private _cd: ChangeDetectorRef
  ) {
    this.withdrawAlert = this.userService.getWithdrawAlert();
    this.ethereumCompatibleBlockChain = new EthereumCompatibleBlockchain();
    this.tonBlockchain = new TonBlockchain();
  }


  refreshWithdrawListFromApi() {
    this.userService.withdrawList().pipe(take(1)).subscribe((list) => {
      this.userService.withdrawRequest.next(list);
    });
  }

  getWithdrawList() {
    switch (this.activeTab) {
      case '0':
        return this.needConfirmationList;
      case '1':
        return this.waitingForProcessingList;
      case '2':
        return this.under10TonRequests;
      case '3':
        return this.hasMessage;
    }
    return this.needConfirmationList;
  }


  getWithdraws(): void {
    this.tableLoading = true;
    this.sub.sink = this.userService.withdrawRequest.subscribe(
      (list) => {
        this.tableLoading = false;
        this.updateWithdrawsList(list);
        this.withdraws = this.getWithdrawList();
        this._cd.markForCheck();
      },
      () => {
        this.tableLoading = false;
      }
    );
  }

  getContractAddress(tokenId: SupportedContract): string {
    return this.contractAddress[tokenId];
  }

  async generateContractTransaction(withdraw: WithdrawModel) {
    try {
      const tronweb = window.tronWeb;
      const amount = new Big(withdraw.amount).sub(withdraw.fee).round(4, Big.roundDown).times(1000000).toNumber();
      const parameter = [
        { type: 'address', value: withdraw.wallet },
        {
          type: 'uint256',
          value: amount
        }
      ];
      const transactionObject = await tronweb.transactionBuilder.triggerSmartContract(
        tronweb.address.toHex(this.getContractAddress(withdraw.currency.id as SupportedContract)),
        'transfer(address,uint256)',
        {},
        parameter,
        tronweb.address.toHex(window.tronWeb.defaultAddress.base58)
      );
      return transactionObject.transaction;
    } catch (err) {
      throw err;
    }
  }

  async getTokenBalance(tokenId: SupportedContract) {
    try {
      const tronWeb = window.tronWeb;

      const contractAddress = this.contractAddress[tokenId];

      // Create a contract instance
      const contract = await tronWeb.contract().at(contractAddress);

      // Get the current wallet address
      const address = tronWeb.defaultAddress.base58;

      // Call the balanceOf function to retrieve the balance
      const balance = await contract.balanceOf(address).call();

      // Convert the balance to readable format
      const decimals = await contract.decimals().call();
      return balance / Math.pow(10, decimals);
    } catch (err) {
      throw err;
    }
  }

  async signAndBrodCastTransaction(transaction: any) {
    try {
      const tronweb = window.tronWeb;
      const signedTx = await tronweb.trx.sign(transaction);
      return await tronweb.trx.sendRawTransaction(signedTx);
    } catch (err) {
      throw err;
    }
  }

  async sendUsingTronLink(withdraw: WithdrawModel) {
    this.tronLinkLoading = true;
    await window.tronWeb.address;
    if (window.tronWeb && window.tronWeb.defaultAddress.base58) {
      const tronweb = window.tronWeb;
      try {
        await window.tronLink.request({ method: 'tron_requestAccounts' });
        const amount = new Big(withdraw.amount).sub(withdraw.fee).round(4, Big.roundDown).times(1000000).toNumber();
        if (withdraw.currency.id === 'TRX') {
          const transaction = await tronweb.transactionBuilder.sendTrx(
            withdraw.wallet,
            amount,
            window.tronWeb.defaultAddress.base58
          );
          const broadCastTx = await this.signAndBrodCastTransaction(transaction);
          this.setTransactionHashInsideWithdraw(withdraw.id, `https://tronscan.org/#/transaction/${broadCastTx.txid}`);
        } else if (withdraw.currency.id === 'USDT' || withdraw.currency.id === 'USDC') {
          const walletBalance = await this.getTokenBalance(withdraw.currency.id);
          if (walletBalance && new Big(walletBalance).times(1000000).toNumber() > amount) {
            const transaction = await this.generateContractTransaction(withdraw);
            const broadCastTx = await this.signAndBrodCastTransaction(transaction);
            this.setTransactionHashInsideWithdraw(withdraw.id, `https://tronscan.org/#/transaction/${broadCastTx.txid}`);
          } else {
            this.Toast.fire({
              icon: 'error',
              text: `موجودی  ${withdraw.currency.name}  کافی نمی‌باشد. موجودی فعلی ${walletBalance}`
            });
          }
        } else {
          this.Toast.fire({
            icon: 'error',
            text: 'در حال حاضر فقط امکان انتقال ترون و تتر و یو‌اس‌دی سی از طریق ترون لینک موجود است.'
          });
        }
      } catch (err: unknown) {
        this.tronLinkLoading = false;
        if (typeof err === 'string') {
          if (err.includes('balance is not sufficient')) {
            this.Toast.fire({
              icon: 'error',
              text: 'موجودی ترون کافی نیست.'
            });
          }
        } else {
          console.log(err);
        }
      }
    }
    this.tronLinkLoading = false;
    this._cd.markForCheck();
  }

  updateWithdrawsList(list: WithdrawModel[]) {
    list.map((withdraw) => {
      const oldWithdraw = [...this.needConfirmationList, ...this.waitingForProcessingList].find((oldWithdraw) => oldWithdraw.id === withdraw.id);
      if (oldWithdraw) {
        withdraw.message = oldWithdraw.message;
      }
    });
    let withdraws = list.sort((a, b) => new Date(Date.parse(a.createdAt.replace(/-/g, '/'))).getTime() - new Date(Date.parse(b.createdAt.replace(/-/g, '/'))).getTime());
    const withdrawBiggerTanXTon = withdraws.filter(withdraw => withdraw.currency.network !== 'TON' || withdraw.amount > 3);

    this.needConfirmationList = withdrawBiggerTanXTon.filter(withdraw => withdraw.status === WithdrawRialStatus.PENDING);
    this.waitingForProcessingList = withdrawBiggerTanXTon.filter(withdraw => withdraw.status === WithdrawRialStatus.PROCESSING);
    this.under10TonRequests = withdraws.filter(withdraw => withdraw.currency.network === 'TON' && withdraw.amount <= 3 && !withdraw.message);
    this.hasMessage = withdraws.filter(withdraw => withdraw.currency.network === 'TON' && withdraw.message && withdraw.status === WithdrawRialStatus.PENDING);
  }

  getCurrencyPrice(withdraw: WithdrawModel, quoteCurrencyId: 'IRR' | 'USDT') {
    const market = this.markets.get(withdraw.currency.id) as {
      usdtPrice: number,
      irrPrice: number
    };
    if (market) {
      if (quoteCurrencyId == 'IRR') {
        return Big(withdraw.amount).minus(withdraw.fee).mul(market.irrPrice).div(10).round(0, Big.roundDown).toNumber();
      }
      if (quoteCurrencyId == 'USDT') {
        return Big(withdraw.amount).minus(withdraw.fee).mul(market.usdtPrice).round(2, Big.roundDown).toNumber();
      }
    }
    return 0;
  }

  updateWithdraw(withdraw: WithdrawModel): void {
    this.saveLoading = true;
    this.errors = {};
    this.selectedWithdraw = withdraw;
    this.sub.sink = this.userService
      .updateWithdraw(withdraw.status, withdraw.id, withdraw.message)
      .subscribe(
        (response: any) => {
          this.Toast.fire({ icon: 'success', text: response.message });
          this.refreshWithdrawListFromApi();
          this.saveLoading = false;
        },
        (response: HttpError) => {
          if (response.status !== 422) {
            this.Toast.fire({ icon: 'error', text: response.error });
          }
          if (response.status === 422) {
            this.errors = response.validationError;
            if (this.errors?.status !== undefined) {
              this.Toast.fire({ icon: 'error', text: this.errors.status[0] });
            }
            if (this.errors?.message !== undefined) {
              this.Toast.fire({ icon: 'error', text: this.errors.message[0] });
            }
          }
          this.saveLoading = false;
        }
      );
  }

  setWithdrawAlert(withdrawAlert: { show: boolean; silent: boolean }): void {
    this.userService.setWithdrawAlert(withdrawAlert);
  }


  copy(text: string, alert: string): void {
    navigator.clipboard.writeText(text);
    this.Toast.fire({ icon: 'success', text: alert });
  }

  changeRecordPerPageLimit(limit: number, type: string): void {
    if (type === 'pending') {
      this.limitPending = limit;
    } else if (type === 'checked') {
      this.limitChecked = limit;
    } else if (type === 'UnderXTonRequests') {
      this.limitUnderXTon = limit;
    } else {
      this.limitHasMessage = limit;
    }

  }


  copyRow(withdraw: WithdrawModel, alert: string): void {
    navigator.clipboard.writeText(
      `${withdraw.createdAt}\t${withdraw?.user?.firstName} ${withdraw?.user?.lastName}\t${
        withdraw?.user?.email
      }\t${withdraw.currency.id}\t${withdraw.currency.network}\t${withdraw.tag ?? '-'}\t${
        withdraw.amount - withdraw.fee
      }\t${withdraw.fee}\t${withdraw.wallet}\t${
        (withdraw.amount - withdraw.fee) * withdraw.currency.usdtPrice
      }\t${(withdraw.amount - withdraw.fee) * withdraw.currency.irrPrice}`
    );
    this.Toast.fire({ icon: 'success', text: alert });
  }

  ngOnInit(): void {
    this.getMarkets();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  openNote(userId: number): void {
    this.dialogService.open(NoteComponent, {
      hasBackdrop: true,
      dialogClass: 'note-modal-container',
      context: {
        userId
      }
    });
  }


  changeWithdrawList(tab: NbTabComponent) {
    this.deSelectAllWithdraw();
    switch (tab.tabId) {
      case '0':
        this.withdraws = this.needConfirmationList;
        break;
      case '1':
        this.withdraws = this.waitingForProcessingList;
        break;
      case '2':
        this.withdraws = this.under10TonRequests;
        break;
      case '3':
        this.withdraws = this.hasMessage;
    }
    this.activeTab = tab.tabId;
  }

  getSeedPhrase(seed: string, dialogRef: NbDialogRef<any>) {
    this.seed = seed;
    dialogRef.close();
    this.sendBatchTransaction();
  }
}
